import { Header } from '@tanstack/react-table';
import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { Z_INDEX } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling';

// Width of the invisible grab zone at the column's trailing edge.
const HIT_AREA_WIDTH = 9;

const HitArea = styled.div`
	position: absolute;
	top: 0;
	right: 0;
	height: 100%;
	width: ${HIT_AREA_WIDTH}px;
	cursor: col-resize;
	user-select: none;
	touch-action: none;
	z-index: 1;
`;

interface LineGeometry {
	left: number;
	top: number;
	height: number;
}

const getScrollParent = (element: HTMLElement | null): HTMLElement | null => {
	let node = element?.parentElement ?? null;
	while (node) {
		const { overflowY } = window.getComputedStyle(node);
		if (overflowY === 'auto' || overflowY === 'scroll') {
			return node;
		}
		node = node.parentElement;
	}
	return null;
};

interface Props<T> {
	header: Header<T, unknown>;
}

export const ColumnResizeHandle = <T,>({ header }: Props<T>) => {
	const hitAreaRef = useRef<HTMLDivElement>(null);
	const [isHovered, setIsHovered] = useState(false);
	const [line, setLine] = useState<LineGeometry | null>(null);
	const isResizing = header.column.getIsResizing();

	// Recompute on every size change so the line tracks the boundary while dragging.
	const size = header.column.getSize();

	const computeLine = useCallback((): LineGeometry | null => {
		const headerCell = hitAreaRef.current?.parentElement;
		if (!headerCell) {
			return null;
		}
		const cellRect = headerCell.getBoundingClientRect();
		const scrollArea = getScrollParent(hitAreaRef.current);
		const scrollRect = scrollArea?.getBoundingClientRect();

		const top = scrollRect?.top ?? cellRect.top;

		// The line should span the table's content — the data rows plus the totals
		// footer — but stop above the horizontal scrollbar and any empty space /
		// pagination beneath. clientHeight excludes the scrollbar, so it marks the
		// bottom of the visible content area.
		const contentBottom =
			scrollArea && scrollRect
				? scrollRect.top + scrollArea.clientHeight
				: scrollRect?.bottom ?? window.innerHeight;

		// Prefer the footer (totals) row's bottom when present, otherwise the last
		// data row; clamp to the content area so the line never overlaps the scrollbar.
		const footer = scrollArea?.querySelector<HTMLElement>('[data-table-footer]');
		const rows = scrollArea?.querySelectorAll<HTMLElement>('[role="row"]');
		const lastRow = rows?.length ? rows[rows.length - 1] : null;
		const contentEnd = footer ?? lastRow;
		const bottom = contentEnd
			? Math.min(contentEnd.getBoundingClientRect().bottom, contentBottom)
			: contentBottom;

		return {
			left: cellRect.right - 1,
			top,
			height: Math.max(0, bottom - top),
		};
	}, []);

	useLayoutEffect(() => {
		setLine(isHovered || isResizing ? computeLine() : null);
	}, [isHovered, isResizing, size, computeLine]);

	const syncSizeBeforeResize = (
		e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>
	) => {
		const headerCell = e.currentTarget.parentElement;
		if (headerCell) {
			const actualWidth = headerCell.getBoundingClientRect().width;
			header.column.columnDef.size = actualWidth;
		}

		const resizeHandler = header.getResizeHandler();
		resizeHandler(e);
	};

	return (
		<HitArea
			ref={hitAreaRef}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
			onMouseDown={syncSizeBeforeResize}
			onTouchStart={syncSizeBeforeResize}
		>
			{line &&
				createPortal(
					<div
						style={{
							position: 'fixed',
							left: line.left,
							top: line.top,
							height: line.height,
							width: 1,
							backgroundColor: isResizing
								? defaultTheme.v3.colors.blue[6]
								: defaultTheme.v3.colors.gray[9],
							opacity: isResizing ? 0.8 : 0.35,
							pointerEvents: 'none',
							zIndex: Z_INDEX.FULLSCREEN_TABLE + 1,
						}}
					/>,
					document.body
				)}
		</HitArea>
	);
};
